import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { addDays, endOfWeek, format, startOfWeek } from "date-fns";
import { dfLocale as fr } from "../../lib/i18n";
import { ChevronLeft, ChevronRight, MapPin, Users, Truck, FileCheck2 } from "lucide-react";
import { StatusBadge } from "../../components/Shared";
import { cachedGet } from "../../lib/offline";
import { MOVE_STATUS } from "../../lib/labels";

const iso = (d) => format(d, "yyyy-MM-dd");

function MoveCard({ m }) {
  return (
    <Link to={`/nomade/chantier/${m.id}`} data-testid={`nomade-move-${m.id}`} className="card-hover block rounded-2xl border border-slate-200 bg-white p-4">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="font-mono-num text-sm font-bold text-[#1559d6]">{m.start_time}</p>
          <p className="font-semibold text-slate-900">{m.title || m.client_name}</p>
        </div>
        <StatusBadge map={MOVE_STATUS} value={m.status} />
      </div>
      <p className="mt-2 flex items-start gap-1.5 text-sm text-slate-600"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-teal-500" />{m.origin || "—"} → {m.destination || "—"}</p>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
        <span>{m.volume_m3} m³</span>
        {m.staff_names.length > 0 && <span className="flex items-center gap-1"><Users className="h-3 w-3" />{m.staff_names.join(", ")}</span>}
        {m.vehicle_names.length > 0 && <span className="flex items-center gap-1"><Truck className="h-3 w-3" />{m.vehicle_names.join(", ")}</span>}
        {m.has_report && <span className="flex items-center gap-1 text-teal-600"><FileCheck2 className="h-3 w-3" />Compte rendu</span>}
      </div>
    </Link>
  );
}

export default function NomadePlanning() {
  const [mode, setMode] = useState("day");
  const [ref, setRef] = useState(new Date());
  const [moves, setMoves] = useState(null);

  const [start, end] = useMemo(() => (mode === "day" ? [ref, ref] : [startOfWeek(ref, { weekStartsOn: 1 }), endOfWeek(ref, { weekStartsOn: 1 })]), [mode, ref]);
  useEffect(() => {
    setMoves(null);
    cachedGet("/field/moves", { start: iso(start), end: iso(end) }).then((r) => setMoves(r.data)).catch(() => setMoves([]));
  }, [start, end]);

  const step = mode === "day" ? 1 : 7;
  const label = mode === "day" ? format(ref, "EEEE d MMMM", { locale: fr }) : `Semaine du ${format(start, "d MMM", { locale: fr })} au ${format(end, "d MMM", { locale: fr })}`;
  const days = mode === "day" ? [iso(ref)] : Array.from({ length: 7 }, (_, i) => iso(addDays(start, i)));

  return (
    <div className="fade-up space-y-4">
      <div className="flex rounded-xl bg-slate-200/70 p-1">
        {[["day", "Aujourd'hui / jour"], ["week", "Semaine"]].map(([k, l]) => (
          <button key={k} data-testid={`nomade-mode-${k}`} onClick={() => setMode(k)} className={`flex-1 rounded-lg py-2 text-sm font-semibold transition-colors ${mode === k ? "bg-white text-[#0b2c6b] shadow-sm" : "text-slate-500"}`}>{l}</button>
        ))}
      </div>
      <div className="flex items-center justify-between">
        <button data-testid="nomade-prev" onClick={() => setRef(addDays(ref, -step))} className="rounded-lg p-2 hover:bg-white"><ChevronLeft className="h-5 w-5" /></button>
        <button onClick={() => setRef(new Date())} className="text-center"><h1 className="text-lg font-bold capitalize text-slate-900" data-testid="nomade-period-label">{label}</h1></button>
        <button data-testid="nomade-next" onClick={() => setRef(addDays(ref, step))} className="rounded-lg p-2 hover:bg-white"><ChevronRight className="h-5 w-5" /></button>
      </div>
      {moves && days.map((d) => {
        const list = moves.filter((m) => m.date === d);
        if (mode === "week" && list.length === 0) return null;
        return (
          <section key={d} className="space-y-3">
            {mode === "week" && <h2 className="pt-2 text-xs font-semibold uppercase tracking-widest text-slate-500">{format(new Date(`${d}T12:00:00`), "EEEE d MMMM", { locale: fr })}</h2>}
            {list.map((m) => <MoveCard key={m.id} m={m} />)}
          </section>
        );
      })}
      {moves && moves.length === 0 && <p className="rounded-2xl border border-dashed border-slate-300 bg-white py-12 text-center text-sm text-slate-500" data-testid="nomade-empty">Aucun déménagement sur cette période</p>}
    </div>
  );
}
